import React from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";

interface VoiceVisualizerOrbProps {
  isRecording?: boolean;
  size?: number;
  glowColor?: string;
  secondaryColor?: string;
}

export const VoiceVisualizerOrb: React.FC<VoiceVisualizerOrbProps> = ({
  isRecording = false,
  size = 72,
  glowColor = "#10B981",
  secondaryColor = "#6366F1",
}) => {
  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Outer Pulse Ring 1 (When recording) */}
      {isRecording && (
        <motion.div
          animate={{
            scale: [1, 1.6, 2],
            opacity: [0.6, 0.25, 0],
          }}
          transition={{
            repeat: Infinity,
            duration: 1.8,
            ease: "easeOut",
          }}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${glowColor}50 0%, transparent 70%)`,
            border: `1.5px solid ${glowColor}60`,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Outer Pulse Ring 2 (Offset when recording) */}
      {isRecording && (
        <motion.div
          animate={{
            scale: [1, 1.4, 1.8],
            opacity: [0.5, 0.2, 0],
          }}
          transition={{
            repeat: Infinity,
            duration: 1.8,
            delay: 0.6,
            ease: "easeOut",
          }}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${secondaryColor}40 0%, transparent 70%)`,
            border: `1px solid ${secondaryColor}50`,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Spinning AI Core Mesh */}
      <motion.div
        animate={{
          rotate: isRecording ? 360 : [0, 180, 360],
          scale: isRecording ? [1, 1.05, 1] : 1,
        }}
        transition={{
          rotate: {
            repeat: Infinity,
            duration: isRecording ? 4 : 12,
            ease: "linear",
          },
          scale: {
            repeat: Infinity,
            duration: 1.2,
            ease: "easeInOut",
          },
        }}
        style={{
          position: "absolute",
          width: "90%",
          height: "90%",
          borderRadius: "50%",
          background: isRecording
            ? `radial-gradient(circle at 30% 30%, ${glowColor}, ${secondaryColor})`
            : `radial-gradient(circle at 30% 30%, #34D399, #0D9488)`,
          filter: `drop-shadow(0 0 ${isRecording ? "14px" : "8px"} ${glowColor}80)`,
          opacity: 0.9,
        }}
      />

      {/* Core Highlight Reflection */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          left: "20%",
          width: "25%",
          height: "25%",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.65)",
          filter: "blur(1.5px)",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

export default VoiceVisualizerOrb;
